import { isEmpty } from 'lodash';
import { VFC } from 'react';
import { Link } from 'react-router-dom-v5-compat';

import { ReportCustomListDetails } from 'dashboard/components/ReportCustomListDetails';
import { AddressLookup } from 'dashboard/components/ReportResult/AddressLookup';
import AdverseMedia from 'dashboard/components/ReportResult/AdverseMedia';
import BusinessAdverseMedia from 'dashboard/components/ReportResult/BusinessAdverseMedia';
import BusinessAssociatedPersons from 'dashboard/components/ReportResult/BusinessAssociatedPersons';
import BusinessClassification from 'dashboard/components/ReportResult/BusinessClassification';
import BusinessIndustryClassification from 'dashboard/components/ReportResult/BusinessIndustryClassification';
import BusinessLiensLookup from 'dashboard/components/ReportResult/BusinessLiensLookup';
import BusinessLookup from 'dashboard/components/ReportResult/BusinessLookup';
import BusinessNonprofit from 'dashboard/components/ReportResult/BusinessNonprofit';
import BusinessOnlinePresence from 'dashboard/components/ReportResult/BusinessOnlinePresence';
import { BusinessRegistrationsLookup } from 'dashboard/components/ReportResult/BusinessRegistrationsLookup';
import BusinessSocialMedia from 'dashboard/components/ReportResult/BusinessSocialMedia';
import BusinessWatchlist from 'dashboard/components/ReportResult/BusinessWatchlist';
import BusinessWebsite from 'dashboard/components/ReportResult/BusinessWebsite';
import ChainalysisAddressScreening from 'dashboard/components/ReportResult/ChainalysisAddressScreening';
import CryptoAddressWatchlist from 'dashboard/components/ReportResult/CryptoAddressWatchlist';
import { EmailAddress } from 'dashboard/components/ReportResult/EmailAddress';
import FinraBrokerCheck from 'dashboard/components/ReportResult/FinraBrokerCheck';
import LoadingResult from 'dashboard/components/ReportResult/LoadingResult';
import MiddeskBusinessLookup from 'dashboard/components/ReportResult/MiddeskBusinessLookup';
import MxAccount from 'dashboard/components/ReportResult/MxAccount';
import PhoneNumber from 'dashboard/components/ReportResult/PhoneNumber';
import PoliticallyExposedPerson from 'dashboard/components/ReportResult/PoliticallyExposedPerson';
import { Profile } from 'dashboard/components/ReportResult/Profile';
import ProfileNonAuthoritative from 'dashboard/components/ReportResult/ProfileNonAuthoritative';
import { ReportError } from 'dashboard/components/ReportResult/ReportError';
import { ReportResultIntegration } from 'dashboard/components/ReportResult/ReportResultIntegration';
import Screening from 'dashboard/components/ReportResult/Screening';
import SecActionLookup from 'dashboard/components/ReportResult/SecActionLookup';
import SentilinkApplicationRisk from 'dashboard/components/ReportResult/SentilinkApplicationRisk';
import SentilinkScores from 'dashboard/components/ReportResult/SentilinkScores';
import SocialMedia from 'dashboard/components/ReportResult/SocialMedia';
import Synthetic from 'dashboard/components/ReportResult/Synthetic';
import Watchlist from 'dashboard/components/ReportResult/Watchlist';
import { Report, ReportStatus } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import useCurrentEnvironmentType from 'lib/hooks/useCurrentEnvironmentType';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

interface ReportResultProps {
  report: Report;
  reportTemplate?: ReportTemplate;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const ReportResult: VFC<ReportResultProps> = ({
  report,
  isRedacted,
  multiCardFormat = false,
}: ReportResultProps) => {
  const { name: environmentName } = useCurrentEnvironmentType();
  if (report.attributes.status === ReportStatus.Errored) {
    // Allows the serialization of the error message to be displayed in the UI
    const hasFailureMessage = !isEmpty(report.attributes['failure-message']);
    const content = (
      <ReportError>
        {hasFailureMessage && (
          <Paragraph color='tertiary'>{report.attributes['failure-message']}</Paragraph>
        )}
        <Paragraph color='tertiary'>
          <a href='https://status.withpersona.com/' target='_blank' rel='noreferrer'>
            Check server status
          </a>{' '}
          or <Link to='/dashboard/contact-us'>contact support</Link>.
        </Paragraph>
        <Spacer height={1} />
      </ReportError>
    );

    return multiCardFormat ? (
      <Card>
        <Card.Body>{content}</Card.Body>
      </Card>
    ) : (
      <>{content}</>
    );
  }

  if (report.attributes.status === ReportStatus.Pending) {
    return <LoadingResult multiCardFormat={multiCardFormat} />;
  }

  switch (report.type) {
    case ApiModel.ReportAddressLookup:
      return (
        <AddressLookup report={report} isRedacted={isRedacted} multiCardFormat={multiCardFormat} />
      );
    case ApiModel.ReportSnapshotAdverseMedia:
    case ApiModel.ReportAdverseMedia:
      return <AdverseMedia report={report} multiCardFormat={multiCardFormat} />;
    case ApiModel.ReportBusinessAdverseMedia:
      return (
        <BusinessAdverseMedia
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportBusinessAssociatedPersons:
      return (
        <BusinessAssociatedPersons
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportBusinessClassification:
      return (
        <BusinessClassification
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportBusinessIndustryClassification:
      return (
        <BusinessIndustryClassification
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportBusinessLiensLookup:
      return <BusinessLiensLookup report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessRegistrationsLookup:
      return <BusinessRegistrationsLookup report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportBusinessLookup:
      return (
        <BusinessLookup report={report} isRedacted={isRedacted} multiCardFormat={multiCardFormat} />
      );
    case ApiModel.ReportSnapshotBusinessNonprofit:
    case ApiModel.ReportBusinessNonprofit:
      return (
        <BusinessNonprofit
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportBusinessOnlinePresence:
      return (
        <BusinessOnlinePresence
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportBusinessSocialMedia:
      return <BusinessSocialMedia report={report} multiCardFormat={multiCardFormat} />;
    case ApiModel.ReportBusinessWatchlist:
      return (
        <BusinessWatchlist
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportBusinessWebsite:
      return (
        <BusinessWebsite
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportChainalysisAddressScreening:
      return (
        <ChainalysisAddressScreening
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportCryptoAddressWatchlist:
      return (
        <CryptoAddressWatchlist
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportCustomList:
      return <ReportCustomListDetails report={report} multiCardFormat={multiCardFormat} />;
    case ApiModel.ReportEmailAddress:
      return <EmailAddress report={report} multiCardFormat={multiCardFormat} />;
    case ApiModel.ReportFinraBrokerCheck:
      return <FinraBrokerCheck report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportMiddeskBusinessLookup:
      return (
        <MiddeskBusinessLookup
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportPhoneNumber:
      return <PhoneNumber report={report} multiCardFormat={multiCardFormat} />;
    case ApiModel.ReportMxAccount:
      return (
        <MxAccount report={report} isRedacted={isRedacted} multiCardFormat={multiCardFormat} />
      );
    case ApiModel.ReportPoliticallyExposedPerson:
      return (
        <PoliticallyExposedPerson
          report={report}
          multiCardFormat={multiCardFormat}
          environmentName={environmentName}
        />
      );
    case ApiModel.ReportProfile:
      return <Profile report={report} multiCardFormat={multiCardFormat} />;
    case ApiModel.ReportProfileNonAuthoritative:
      return (
        <ProfileNonAuthoritative
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportScreening:
      return (
        <Screening
          report={report}
          // TODO(maggie): uncomment when Screening component is updated
          // isRedacted={isRedacted}
          // multiCardFormat={multiCardFormat}
          // environmentName={environmentName}
        />
      );

    case ApiModel.ReportSecActionLookup:
      return <SecActionLookup report={report} isRedacted={isRedacted} />;
    case ApiModel.ReportSentilinkApplicationRisk:
      return (
        <SentilinkApplicationRisk
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportSentilinkScores:
      return (
        <SentilinkScores
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.ReportSocialMedia:
      return <SocialMedia report={report} multiCardFormat={multiCardFormat} />;
    case ApiModel.ReportSynthetic:
      return (
        <Synthetic report={report} isRedacted={isRedacted} multiCardFormat={multiCardFormat} />
      );
    case ApiModel.ReportWatchlist:
      return (
        <Watchlist
          report={report}
          multiCardFormat={multiCardFormat}
          environmentName={environmentName}
        />
      );
    case ApiModel.ReportSnapshotWatchlist:
      return (
        <Watchlist
          report={report}
          multiCardFormat={multiCardFormat}
          environmentName={environmentName}
          readOnly
        />
      );
    case ApiModel.ReportClearbitBusinessLookup:
    case ApiModel.ReportCoinbaseCheckCryptoRisk:
    case ApiModel.ReportEquifaxOneview:
    case ApiModel.ReportKyckrBusinessLookup:
    case ApiModel.ReportTrmWalletScreening:
      return (
        <ReportResultIntegration
          report={report}
          isRedacted={isRedacted}
          multiCardFormat={multiCardFormat}
        />
      );
    case ApiModel.Report:
      return null;
    default:
      assertNever(report);
  }
};

export default ReportResult;
